import {
  PdfTemplate,
  PdfTemplateElement,
  PdfTemplateElementType,
  PdfTemplateLabel,
  PdfTemplatePage,
} from '../entities/form-pdf-template.entity';
import { Form } from '../entities/form.entity';
import {
  PDF_TABLE_CONTINUATION_Y_MM,
  PDF_TABLE_SAFETY_MARGIN_MM,
} from '../models/pdf-table-pagination.model';
import {
  findFormFieldByLabel,
  getPdfBodyHeightMm,
  getPdfPageWidthMm,
  getTagDataItem,
  wrapTextToLines,
} from './pdf-table-pagination.helper';

const PT_TO_MM = 0.352778;
const LINE_HEIGHT_FACTOR = 1.2;
const CHAR_WIDTH_FACTOR = 0.45;
const BULLET_INDENT_MM = 6;
const ITEM_GAP_MM = 0.6;
const LIST_BLOCK_CHROME_MM = 3;

export interface PdfBoundListSliceMeta {
  listSliceItems?: string[];
  listIsContinuation?: boolean;
}

export type PdfTemplateLabelWithListSlice = PdfTemplateLabel &
  PdfBoundListSliceMeta;

export interface PdfListPageSlice {
  index: number;
  items: string[];
  isFirst: boolean;
  isContinuation: boolean;
  isLast: boolean;
}

export interface PdfBoundListPaginationOptions {
  items: string[];
  fontSizePt: number;
  listWidthMm: number;
  firstPageAvailableHeightMm: number;
  continuationPageAvailableHeightMm: number;
  safetyMarginMm?: number;
}

export function getBoundListSliceMeta(
  element: PdfTemplateElement
): PdfBoundListSliceMeta {
  const meta = element as PdfTemplateLabelWithListSlice;
  return {
    listSliceItems: meta.listSliceItems,
    listIsContinuation: meta.listIsContinuation,
  };
}

export function isBoundListLabel(
  element: PdfTemplateElement,
  form?: Form | null
): boolean {
  if (element.type !== PdfTemplateElementType.LABEL) {
    return false;
  }

  const label = element as PdfTemplateLabel;
  if (!label.bindingTagValue && !label.bindingTagUuid) {
    return false;
  }

  if (label.fieldType === 'multiple-choose') {
    return true;
  }

  const field = findFormFieldByLabel(form, label);
  return field?.type === 'multiple-choose';
}

export function extractBoundListItems(tagDataItem: unknown): string[] {
  const raw = unwrapListValue(tagDataItem);
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((item) => {
      if (item === null || item === undefined) {
        return '';
      }
      if (typeof item === 'string' || typeof item === 'number') {
        return String(item);
      }
      return '';
    })
    .filter((item) => item.trim() !== '');
}

export function paginateBoundList(
  options: PdfBoundListPaginationOptions
): PdfListPageSlice[] {
  const sourceItems = options.items || [];
  const safetyMargin = options.safetyMarginMm ?? PDF_TABLE_SAFETY_MARGIN_MM;

  if (sourceItems.length === 0) {
    return [
      {
        index: 0,
        items: [],
        isFirst: true,
        isContinuation: false,
        isLast: true,
      },
    ];
  }

  const queue = [...sourceItems];
  const packedSlices: string[][] = [];
  let isFirst = true;

  while (queue.length > 0) {
    const availableHeight = isFirst
      ? options.firstPageAvailableHeightMm
      : options.continuationPageAvailableHeightMm;
    const budget = Math.max(
      availableHeight - safetyMargin - LIST_BLOCK_CHROME_MM,
      0
    );
    const packed: string[] = [];
    let usedHeight = 0;

    while (queue.length > 0) {
      const item = queue[0];
      const itemHeight = estimateItemHeightMm(
        item,
        options.listWidthMm,
        options.fontSizePt
      );

      if (packed.length === 0) {
        packed.push(item);
        queue.shift();
        usedHeight += itemHeight;
        if (itemHeight > budget) {
          break;
        }
        continue;
      }

      if (usedHeight + itemHeight <= budget) {
        packed.push(item);
        queue.shift();
        usedHeight += itemHeight;
        continue;
      }

      break;
    }

    packedSlices.push(packed);
    isFirst = false;
  }

  return packedSlices.map((items, index) => {
    const isLast = index === packedSlices.length - 1;
    return {
      index,
      items,
      isFirst: index === 0,
      isContinuation: index > 0,
      isLast,
    };
  });
}

export function expandPdfPagesForBoundLists(
  pages: PdfTemplatePage[],
  template: PdfTemplate,
  tagData: { [tag: string]: any } = {},
  form?: Form | null
): PdfTemplatePage[] {
  if (!pages?.length) {
    return [];
  }

  const expanded: PdfTemplatePage[] = [];

  pages.forEach((page) => {
    const bodyElements = page.body?.elements || [];
    const listLabels = bodyElements.filter((element) =>
      isBoundListLabel(element, form)
    ) as PdfTemplateLabel[];

    if (listLabels.length === 0) {
      expanded.push(clonePage(page));
      return;
    }

    const bodyHeightMm = getPdfBodyHeightMm(page, template);
    const pageWidthMm = getPdfPageWidthMm(template);
    const firstPage = clonePage(page);
    const continuationJobs: {
      label: PdfTemplateLabel;
      slices: PdfListPageSlice[];
    }[] = [];

    listLabels
      .slice()
      .sort((a, b) => a.y - b.y || a.x - b.x)
      .forEach((label) => {
        const items = extractBoundListItems(getTagDataItem(tagData, label));
        if (items.length === 0) {
          return;
        }

        const firstAvailable = getFirstPageAvailableHeightMm(
          label,
          bodyElements,
          bodyHeightMm
        );
        const continuationAvailable = Math.max(
          bodyHeightMm - PDF_TABLE_CONTINUATION_Y_MM,
          8
        );
        const listWidthMm = label.width || Math.max(pageWidthMm - label.x, 20);
        const slices = paginateBoundList({
          items,
          fontSizePt: label.fontSize || 12,
          listWidthMm,
          firstPageAvailableHeightMm: firstAvailable,
          continuationPageAvailableHeightMm: continuationAvailable,
        });

        applySliceMetaToPage(firstPage, label.uuid, slices[0]);

        if (slices.length > 1) {
          continuationJobs.push({
            label,
            slices: slices.slice(1),
          });
        }
      });

    expanded.push(firstPage);

    continuationJobs.forEach(({ label, slices }) => {
      slices.forEach((slice) => {
        expanded.push(createContinuationPage(firstPage, label, slice));
      });
    });
  });

  expanded.forEach((page, index) => {
    page.order = index;
  });

  return expanded;
}

function unwrapListValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value;
  }
  if (
    value &&
    typeof value === 'object' &&
    'value' in (value as Record<string, unknown>)
  ) {
    return (value as { value: unknown }).value;
  }
  return value;
}

function applySliceMetaToPage(
  page: PdfTemplatePage,
  labelUuid: string,
  slice: PdfListPageSlice
): void {
  const target = page.body?.elements?.find(
    (element) => element.uuid === labelUuid
  ) as PdfTemplateLabelWithListSlice | undefined;

  if (!target) {
    return;
  }

  target.listSliceItems = slice.items;
  target.listIsContinuation = slice.isContinuation;
  target.height = undefined;
}

function createContinuationPage(
  sourcePage: PdfTemplatePage,
  label: PdfTemplateLabel,
  slice: PdfListPageSlice
): PdfTemplatePage {
  const cloned = clonePage(sourcePage);
  const continuationLabel = cloned.body?.elements?.find(
    (element) => element.uuid === label.uuid
  ) as PdfTemplateLabelWithListSlice | undefined;

  if (continuationLabel) {
    continuationLabel.uuid = `${label.uuid}__list__${slice.index}`;
    continuationLabel.y = PDF_TABLE_CONTINUATION_Y_MM;
    continuationLabel.height = undefined;
    continuationLabel.listSliceItems = slice.items;
    continuationLabel.listIsContinuation = true;
  }

  cloned.body = {
    ...cloned.body,
    elements: (cloned.body?.elements || []).filter((element) => {
      if (element.uuid === continuationLabel?.uuid) {
        return true;
      }
      return (
        element.type === PdfTemplateElementType.SHAPE ||
        element.type === PdfTemplateElementType.LINE
      );
    }),
  };

  cloned.uuid = `${sourcePage.uuid}__${label.uuid}__list__${slice.index}`;
  return cloned;
}

function clonePage(page: PdfTemplatePage): PdfTemplatePage {
  return JSON.parse(JSON.stringify(page));
}

function getFirstPageAvailableHeightMm(
  element: PdfTemplateElement,
  bodyElements: PdfTemplateElement[],
  bodyHeightMm: number
): number {
  const nextElement = [...bodyElements]
    .filter(
      (candidate) =>
        candidate.uuid !== element.uuid &&
        candidate.type !== PdfTemplateElementType.LINE &&
        candidate.type !== PdfTemplateElementType.SHAPE
    )
    .sort((a, b) => a.y - b.y)
    .find((candidate) => candidate.y > element.y);

  const ceiling = nextElement ? nextElement.y : bodyHeightMm;
  const available = ceiling - (element.y || 0);

  return Math.max(available, 8);
}

function estimateLineHeightMm(fontSizePt: number): number {
  return fontSizePt * PT_TO_MM * LINE_HEIGHT_FACTOR;
}

function charsPerLine(widthMm: number, fontSizePt: number): number {
  const usableWidthMm = Math.max(widthMm - BULLET_INDENT_MM, 8);
  const charWidthMm = Math.max(fontSizePt * PT_TO_MM * CHAR_WIDTH_FACTOR, 0.5);
  return Math.max(8, Math.floor(usableWidthMm / charWidthMm));
}

function estimateItemHeightMm(
  text: string,
  widthMm: number,
  fontSizePt: number
): number {
  const lines = wrapTextToLines(text, charsPerLine(widthMm, fontSizePt));
  return Math.max(1, lines.length) * estimateLineHeightMm(fontSizePt) + ITEM_GAP_MM;
}
